import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  GoogleMap,
  DirectionsRenderer,
  OverlayView,
  useJsApiLoader,
  InfoWindow,
} from "@react-google-maps/api";
import { FaRegDotCircle } from "react-icons/fa";
import { getAddressByLatLng } from "./MapFunction";
import "./map.css";
import { useSelector } from "react-redux";
const API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;
const MAX_WAYPOINTS = 23;


const darkModeStyle = [
  { elementType: "geometry", stylers: [{ color: "#010113" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#757575" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#212121" }] },
  {
    featureType: "administrative",
    elementType: "geometry",
    stylers: [{ color: "#757575" }],
  },
  {
    featureType: "poi",
    elementType: "labels.text.fill",
    stylers: [{ color: "#757575" }],
  },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ color: "#383838" }],
  },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: "#000000" }],
  },
  {
    featureType: "water",
    elementType: "labels.text.fill",
    stylers: [{ color: "#3d3d3d" }],
  },
];

const MapWithDynamicPointNames = ({ waypoint }) => {

  const themeStore = useSelector((state) => state.theme)

  const [isOrigin, setIsOrigin] = useState(false);
  const [mapCenter, setMapCenter] = useState(null);
  const [waypoints, setWaypoints] = useState([]);
  const [center, setCenter] = useState(null);
  const [origin, setOrigin] = useState(null);
  const [destination, setDestination] = useState(null);
  const [directionsResponses, setDirectionsResponses] = useState([]);
  const [markerLat, setMarkerLat] = useState(null);
  const [markerLng, setMarkerLng] = useState(null);
  const [locationInfo, setLocationInfo] = useState("");
  const mapContainerStyle = { width: "100%", height: "100%" };

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: API_KEY,
  });
  const mapRef = useRef(null);
  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  useEffect(() => {
    if (!waypoint || waypoint.length === 0) return;

    const newArr = waypoint.map((row) => ({
      location: {
        lat: parseFloat(row.latitude),
        lng: parseFloat(row.longitude),
      },
      stopover: true,
    }));

    if (newArr.length > 0) {
      setCenter(newArr[0]);
      setOrigin(newArr[0]);
      setDestination(newArr[newArr.length - 1]);
      setWaypoints(newArr);
    }
  }, [waypoint]);

  const splitWaypoints = (allWaypoints) => {
    const chunks = [];
    for (let i = 0; i < allWaypoints.length; i += MAX_WAYPOINTS) {
      chunks.push(allWaypoints.slice(i, i + MAX_WAYPOINTS));
    }
    return chunks;
  };

  useEffect(() => {
    if (!isLoaded || !origin || !destination) return;

    const waypointChunks = splitWaypoints(waypoints);

    const requests = waypointChunks.map((chunk, index) => {
      const chunkOrigin =
        index === 0
          ? origin.location
          : waypoints[index * MAX_WAYPOINTS - 1].location;
      const chunkDestination = chunk[chunk.length - 1].location;

      const chunkWaypoints =
        index === 0
          ? chunk.slice(0, chunk.length - 1)
          : chunk.slice(1, chunk.length - 1);

      return {
        origin: chunkOrigin,
        destination: chunkDestination,
        travelMode: "DRIVING",
        waypoints: chunkWaypoints,
        optimizeWaypoints: true,
      };
    });

    const service = new window.google.maps.DirectionsService();

    const fetchDirectionsPromises = requests.map(
      (request) =>
        new Promise((resolve, reject) => {
          service.route(request, (response, status) => {
            if (status === "OK") {
              resolve(response);
            } else {
              reject(`Directions request failed with status: ${status}`);
            }
          });
        })
    );

    Promise.all(fetchDirectionsPromises)
      .then((responses) => {
        setDirectionsResponses([...responses]);
        setIsOrigin(true);
        setMapCenter({
          lat: parseFloat(center.location.lat),
          lng: parseFloat(center.location.lng),
        });
      })
      .catch((error) => console.error(error));
  }, [isLoaded, origin, destination, waypoints, center]);

  useEffect(() => {
    if (!isLoaded || !origin || !destination) return;

    const bounds = new window.google.maps.LatLngBounds();
    bounds.extend(
      new window.google.maps.LatLng(origin.location.lat, origin.location.lng)
    );
    bounds.extend(
      new window.google.maps.LatLng(
        destination.location.lat,
        destination.location.lng
      )
    );

    if (waypoints.length > 0) {
      waypoints.forEach((wp) => {
        bounds.extend(
          new window.google.maps.LatLng(wp.location.lat, wp.location.lng)
        );
      });
    }

    setMapCenter({
      lat: (origin.location.lat + destination.location.lat) / 2,
      lng: (origin.location.lng + destination.location.lng) / 2,
    });

    setTimeout(() => {
      if (window.google && window.google.maps && mapRef.current) {
        mapRef.current.fitBounds(bounds);
      }
    }, 500);
  }, [isLoaded, origin, destination, waypoints]);

  const mapOptions = {
      streetViewControl: false,
      mapTypeControl: false,
      minZoom: 5,
      maxZoom: 18,
      zoomControl: true,
      zoomControlOptions: {
          position: 6,
      },
      fullscreenControl: true,
      fullscreenControlOptions: {
          position: 1,
      },
      draggable: true,
      scrollwheel: true,
      disableDefaultUI: true,
      clickableIcons: false,
  };

  const debounce = (func, delay) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
  };

  const handleMouseOver = async (marker) => {
    const address = await getAddressByLatLng(marker);
    setMarkerLat(marker.lat);
    setMarkerLng(marker.lng);
    setLocationInfo(address);
  };

  const handleMouseOut = () => {
    setMarkerLat(null);
    setMarkerLng(null);
    setLocationInfo("");
  };

  const debounceHandleMouseOver = useCallback(
    debounce(handleMouseOver, 100),
    []
  );
  const debounceHandleMouseOut = useCallback(debounce(handleMouseOut, 100), []);

  return isLoaded ? (
    <GoogleMap
      mapContainerStyle={mapContainerStyle}
      center={mapCenter || { lat: 12.971188033489787, lng: 77.57729176601477 }}
      zoom={15}
      onLoad={onMapLoad}
      options={{ styles: themeStore.theme == "light" ? null : darkModeStyle, disableDefaultUI: true, mapOptions }}
    >
      {directionsResponses.map((response, index) => (
        <DirectionsRenderer
          key={`directions-${index}-${JSON.stringify(
            response.routes[0].overview_polyline
          )}`}
          options={{
            directions: response,
            preserveViewport: true,
            suppressMarkers: true,
            polylineOptions: {
              strokeColor: "blue",
              strokeWeight: 5,
              strokeOpacity: 0.7,
            },
          }}
        />
      ))}

      {isOrigin && (
        <>
          <OverlayView
            position={{
              lat: parseFloat(origin.location.lat),
              lng: parseFloat(origin.location.lng),
            }} // Use structured coordinates
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            getPixelPositionOffset={(width, height) => ({
              x: -width / 2,
              y: -height,
            })}
          >
            <div
              className="custom-marker"
              style={{
                transform: "translate(-50%, -100%)",
                borderRadius: "50%",
                background:
                  "linear-gradient(to bottom, black 0%, black 46%, white 46%, white 54%, #2ecc71 54%, #2ecc71 100%)",
              }}
              onMouseOver={() =>
                debounceHandleMouseOver({
                  lat: parseFloat(origin.location.lat),
                  lng: parseFloat(origin.location.lng),
                })
              }
              onMouseOut={debounceHandleMouseOut}
            >
              <div className="marker-container">
                <img
                  src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTQEZrATmgHOi5ls0YCCQBTkocia_atSw0X-Q&s"
                  alt="marker"
                  className="marker-img"
                />
              </div>
              <div className="marker-pointer green">
                <FaRegDotCircle
                  style={{
                    fontSize: "7px",
                    marginLeft: "-2.5px",
                    marginTop: "-7px",
                  }}
                />
              </div>
            </div>
          </OverlayView>

          <OverlayView
            position={{
              lat: parseFloat(destination.location.lat),
              lng: parseFloat(destination.location.lng),
            }} // Use structured coordinates
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            getPixelPositionOffset={(width, height) => ({
              x: -width / 2,
              y: -height,
            })}
          >
            <div
              className="custom-marker"
              style={{
                transform: "translate(-50%, -100%)",
                borderRadius: "50%",
                background:
                  "linear-gradient(to bottom, black 0%, black 46%, white 46%, white 54%, #fc8702 54%, #fc8702 100%)",
              }}
              onMouseOver={() =>
                debounceHandleMouseOver({
                  lat: parseFloat(destination.location.lat),
                  lng: parseFloat(destination.location.lng),
                })
              }
              onMouseOut={debounceHandleMouseOut}
            >
              <div className="marker-container">
                <img
                  src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTQEZrATmgHOi5ls0YCCQBTkocia_atSw0X-Q&s"
                  alt="marker"
                  className="marker-img"
                />
              </div>
              <div className="marker-pointer">
                <FaRegDotCircle
                  style={{
                    fontSize: "7px",
                    marginLeft: "-2.5px",
                    marginTop: "-7px",
                  }}
                />
              </div>
            </div>
          </OverlayView>

          {locationInfo && (
            <InfoWindow
              position={{
                lat: parseFloat(markerLat),
                lng: parseFloat(markerLng),
              }}
              options={{ pixelOffset: new window.google.maps.Size(0, -45) }}
            >
              <div
                style={{
                  padding: "10px",
                  paddingTop: "0px",
                  maxWidth: "auto",
                  borderRadius: "10px",
                  backgroundColor: "#fff",
                  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                }}
              >
                <p
                  style={{
                    margin: "0",
                    fontSize: "14px",
                    color: "#555",
                    textAlign: "center",
                  }}
                >
                  {locationInfo}
                </p>
              </div>
            </InfoWindow>
          )}
        </>
      )}
    </GoogleMap>
  ) : (
    <></>
  );
};

export default MapWithDynamicPointNames;
